import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import { getApiPath, webStorage } from 'sidecar-utility-library'
import { maxFileSize } from 'sidecar-utilities'

import { initialState, types } from './types'
import { replaceObjectInArray, removeDocumentFromSpecificUser } from './utils'

export const clearDocuments = () => ({
  type: types.CLEAR_DOCUMENTS,
})

export const deleteDocument = (memberUuid, uuid) => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `documents/${uuid}/members/${memberUuid}`),
    method: 'DELETE',
    types: [
      types.DELETE_DOCUMENT_REQUEST,
      { type: types.DELETE_DOCUMENT_SUCCESS, meta: { memberUuid, uuid } },
      types.DELETE_DOCUMENT_ERROR,
    ],
  },
})

export const fetchDocuments = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `documents/accounts/${webStorage.getItem('aud')}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_DOCUMENTS_REQUEST,
      types.FETCH_DOCUMENTS_SUCCESS,
      types.FETCH_DOCUMENTS_ERROR,
    ],
  },
})

export const uploadDocumentValidationError = error => {
  let filesizeError = null
  if (error?.filesExceededSizeLimit) {
    const filePluralSuffix = error.filesExceededSizeLimit.length > 1 ? 's' : ''
    const fileList = error.filesExceededSizeLimit
      .map(fileName => fileName)
      .join(', ')
    filesizeError = `File${filePluralSuffix} exceed the ${maxFileSize.toString} max size limit: ${fileList}`
  }
  const errorMessage = filesizeError || 'Something went wrong.'
  return {
    type: types.UPLOAD_DOCUMENT_ERROR,
    payload: { response: { message: errorMessage } },
  }
}

export const uploadDocument = (memberUuid, formData) => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `documents?memberUuid=${memberUuid}`),
    method: 'POST',
    headers: {
      'Content-Type': 'IGNORE',
    },
    body: formData,
    types: [
      types.UPLOAD_DOCUMENT_REQUEST,
      types.UPLOAD_DOCUMENT_SUCCESS,
      types.UPLOAD_DOCUMENT_ERROR,
    ],
  },
})

export const viewDocument = (memberUuid, uuid) => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `documents/${uuid}/members/${memberUuid}`),
    method: 'GET',
    types: [
      types.VIEW_DOCUMENT_REQUEST,
      types.VIEW_DOCUMENT_SUCCESS,
      types.VIEW_DOCUMENT_ERROR,
    ],
  },
})

export const clearDeleteError = () => ({
  type: types.CLEAR_DELETE_ERRORS,
})

export const clearUploadError = () => ({
  type: types.CLEAR_UPLOAD_ERRORS,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.CLEAR_DOCUMENTS: {
      return {
        ...state,
        documents: initialState.documents,
      }
    }

    case types.FETCH_DOCUMENTS_REQUEST: {
      return {
        ...state,
        documents: {
          ...state.documents,
          busy: true,
          error: '',
          viewError: '',
        },
      }
    }

    case types.DELETE_DOCUMENT_REQUEST: {
      return {
        ...state,
        documents: {
          ...state.documents,
          deleteBusy: true,
          deleteError: '',
          viewError: '',
        },
      }
    }

    case types.UPLOAD_DOCUMENT_REQUEST: {
      return {
        ...state,
        documents: {
          ...state.documents,
          uploadBusy: true,
          uploadError: '',
          viewError: '',
        },
      }
    }

    case types.VIEW_DOCUMENT_REQUEST: {
      return {
        ...state,
        documents: {
          ...state.documents,
          viewBusy: true,
          viewError: '',
        },
      }
    }

    case types.FETCH_DOCUMENTS_SUCCESS: {
      return {
        ...state,
        documents: {
          ...state.documents,
          busy: false,
          entities: payload,
          error: '',
        },
      }
    }

    case types.UPLOAD_DOCUMENT_SUCCESS: {
      const nextEntities = replaceObjectInArray(
        state.documents.entities,
        payload.member.uuid,
        payload,
      )
      return {
        ...state,
        documents: {
          ...state.documents,
          uploadBusy: false,
          entities: nextEntities,
          uploadError: '',
        },
      }
    }

    case types.DELETE_DOCUMENT_SUCCESS: {
      const { memberUuid, uuid } = meta

      const nextEntities = removeDocumentFromSpecificUser(
        state.documents.entities,
        memberUuid,
        uuid,
      )

      return {
        ...state,
        documents: {
          ...state.documents,
          deleteBusy: false,
          entities: nextEntities,
          deleteError: '',
        },
      }
    }

    case types.VIEW_DOCUMENT_SUCCESS: {
      return {
        ...state,
        documents: {
          ...state.documents,
          viewBusy: false,
          url: payload.url,
          viewError: '',
        },
      }
    }

    case types.FETCH_DOCUMENTS_ERROR: {
      return {
        ...state,
        documents: {
          ...state.documents,
          busy: false,
          error: R.pathOr(
            'Something went wrong.',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.DELETE_DOCUMENT_ERROR: {
      return {
        ...state,
        documents: {
          ...state.documents,
          deleteBusy: false,
          deleteError: R.pathOr(
            'Something went wrong.',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.UPLOAD_DOCUMENT_ERROR: {
      return {
        ...state,
        documents: {
          ...state.documents,
          uploadBusy: false,
          uploadError: R.pathOr(
            'Something went wrong.',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.VIEW_DOCUMENT_ERROR: {
      return {
        ...state,
        documents: {
          ...state.documents,
          viewBusy: false,
          viewError: R.pathOr(
            'Something went wrong.',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.CLEAR_DELETE_ERRORS: {
      return {
        ...state,
        documents: {
          ...state.documents,
          deleteError: '',
        },
      }
    }

    case types.CLEAR_UPLOAD_ERRORS: {
      return {
        ...state,
        documents: {
          ...state.documents,
          uploadError: '',
        },
      }
    }

    default:
      return state
  }
}
